import { motion } from 'motion/react'
import { ArrowLeft, ArrowUpRight, Mail, Users } from 'lucide-react'
import { Link } from 'react-router-dom'
import Header from '../components/site/Header'
import Footer from '../components/site/Footer'
import { useLanguage } from '@/i18n/LanguageContext'
import useSEO from '../hooks/useSEO'
import usePrerenderReady from '../hooks/usePrerenderReady'

type PartnerLink = { label: string; url: string }
type Partner = { place: string; text: string; code?: string; codeLabel?: string; links: PartnerLink[] }
type SimpleBlock = { title: string; role: string; text: string; linkLabel: string; url: string }

function ExternalLink({ href, children, className = '' }: { href: string; children: React.ReactNode; className?: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center gap-1.5 font-semibold text-terracotta transition-colors hover:underline ${className}`}
    >
      {children}
      <ArrowUpRight className="h-4 w-4 shrink-0" />
    </a>
  )
}

function RolePill({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-2 rounded-full border border-terracotta/30 bg-terracotta/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-terracotta">
      <Users className="h-3.5 w-3.5" />
      {children}
    </span>
  )
}

function BlockHeading({ role, title, illustration }: { role: string; title: string; illustration?: string }) {
  return (
    <div className="flex items-center gap-6">
      <div className="flex-1">
        <RolePill>{role}</RolePill>
        <h2 className="mt-4 font-display text-3xl leading-tight md:text-4xl">{title}</h2>
      </div>
      {illustration ? (
        <img
          src={illustration}
          alt=""
          width={160}
          height={160}
          loading="lazy"
          aria-hidden="true"
          className="hidden h-[160px] w-[160px] shrink-0 rounded-full bg-cream shadow-[0_10px_30px_-15px_rgba(15,43,70,0.35)] sm:block"
        />
      ) : null}
    </div>
  )
}

export default function CollaborationsPage() {
  const { t } = useLanguage()
  const copy = t.collaborations

  useSEO({ title: 'Collaborazioni — Fra un viaggio e l\u2019altro', description: copy.intro })
  usePrerenderReady()

  const foodTours: { title: string; role: string; intro: string; partners: Partner[] } = copy.foodTours
  const village: SimpleBlock = copy.village
  const realWanderlust: SimpleBlock = copy.realWanderlust
  const aurea = copy.aurea
  const weroad: SimpleBlock = copy.weroad
  const theVoyage: SimpleBlock = copy.theVoyage

  return (
    <div id="top" className="min-h-screen bg-cream text-ink">
      <Header />
      <main className="pt-32">
        <section className="mx-auto w-[90%] max-w-6xl pb-20 md:pb-28">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="w-full">
            <div className="flex items-center justify-between gap-6">
              <p className="text-sm font-medium uppercase tracking-[0.25em] text-terracotta">{copy.eyebrow}</p>
              <Link to="/" className="group inline-flex shrink-0 items-center gap-3 rounded-full border border-ink/15 bg-white/60 px-5 py-3 text-sm font-semibold text-ink/70 shadow-sm backdrop-blur-sm transition-all duration-300 hover:-translate-y-0.5 hover:border-terracotta/40 hover:bg-white hover:text-terracotta hover:shadow-md">
                <ArrowLeft className="h-4 w-4" /> {copy.backHome}
              </Link>
            </div>
            <h1 className="mt-7 w-full font-display text-5xl leading-tight md:text-7xl">
              {copy.titleBefore} <span className="italic text-terracotta">{copy.titleAccent}</span>
            </h1>
            <p className="mt-7 text-lg leading-relaxed text-ink/70">{copy.intro}</p>
          </motion.div>

          {/* Food tour */}
          <motion.section
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.6 }}
            className="mt-16 rounded-[2rem] border border-ink/10 bg-white p-8 shadow-[0_18px_50px_-25px_rgba(15,43,70,0.28)] md:p-12"
          >
            <BlockHeading role={foodTours.role} title={foodTours.title} illustration="/images/collab-foodtour.svg" />
            <p className="mt-5 text-lg leading-relaxed text-ink/70">{foodTours.intro}</p>
            <div className="mt-10 grid gap-6 md:grid-cols-2">
              {foodTours.partners.map((partner) => (
                <div key={partner.place} className="flex flex-col rounded-3xl border border-ink/10 bg-cream p-7">
                  <h3 className="font-display text-2xl leading-snug">{partner.place}</h3>
                  <p className="mt-3 flex-1 leading-relaxed text-ink/70">{partner.text}</p>
                  {partner.code ? (
                    <div className="mt-5">
                      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-ink/50">{partner.codeLabel}</p>
                      <span className="mt-2 inline-block rounded-full bg-terracotta/10 px-4 py-1.5 font-mono text-sm font-semibold text-terracotta">
                        {partner.code}
                      </span>
                    </div>
                  ) : null}
                  <div className="mt-5 flex flex-col items-start gap-2 border-t border-ink/10 pt-5">
                    {partner.links.map((link) => (
                      <ExternalLink key={link.url} href={link.url}>
                        {link.label}
                      </ExternalLink>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </motion.section>

          {/* The Voyage */}
          <motion.section
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.6 }}
            className="mt-10 rounded-[2rem] border border-terracotta/20 bg-terracotta/10 p-8 shadow-[0_18px_50px_-25px_rgba(15,43,70,0.28)] md:p-12"
          >
            <BlockHeading role={theVoyage.role} title={theVoyage.title} illustration="/images/collab-thevoyage.svg" />
            <p className="mt-5 max-w-3xl text-lg leading-relaxed text-ink/75">{theVoyage.text}</p>
            <div className="mt-7">
              <ExternalLink href={theVoyage.url}>{theVoyage.linkLabel}</ExternalLink>
            </div>
          </motion.section>

          {/* The Village */}
          <motion.section
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.6 }}
            className="mt-10 rounded-[2rem] border border-ink/10 bg-white p-8 shadow-[0_18px_50px_-25px_rgba(15,43,70,0.28)] md:p-12"
          >
            <BlockHeading role={village.role} title={village.title} illustration="/images/collab-brisighella.svg" />
            <p className="mt-5 max-w-3xl text-lg leading-relaxed text-ink/75">{village.text}</p>
            <div className="mt-7">
              <ExternalLink href={village.url}>{village.linkLabel}</ExternalLink>
            </div>
          </motion.section>

          {/* Real Wanderlust */}
          <motion.section
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.6 }}
            className="mt-10 rounded-[2rem] border border-terracotta/20 bg-terracotta/10 p-8 shadow-[0_18px_50px_-25px_rgba(15,43,70,0.28)] md:p-12"
          >
            <BlockHeading role={realWanderlust.role} title={realWanderlust.title} illustration="/images/collab-marocco.svg" />
            <p className="mt-5 max-w-3xl text-lg leading-relaxed text-ink/75">{realWanderlust.text}</p>
            <div className="mt-7">
              <ExternalLink href={realWanderlust.url}>{realWanderlust.linkLabel}</ExternalLink>
            </div>
          </motion.section>

          {/* Aurea Viaggi */}
          <motion.section
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.6 }}
            className="mt-10 rounded-[2rem] border border-ink/10 bg-white p-8 shadow-[0_18px_50px_-25px_rgba(15,43,70,0.28)] md:p-12"
          >
            <BlockHeading role={aurea.role} title={aurea.title} illustration="/images/collab-aurea.svg" />
            <p className="mt-5 max-w-3xl text-lg leading-relaxed text-ink/75">{aurea.text}</p>
            <div className="mt-7">
              <ExternalLink href={aurea.url}>{aurea.linkLabel}</ExternalLink>
            </div>
            <div className="mt-8 rounded-3xl border border-terracotta/25 bg-terracotta/5 p-6">
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-terracotta">{aurea.tour.eyebrow}</p>
              <h3 className="mt-3 font-display text-2xl leading-snug md:text-3xl">{aurea.tour.title}</h3>
              <p className="mt-3 leading-relaxed text-ink/75">{aurea.tour.text}</p>
              <a
                href={`mailto:${aurea.tour.email}?subject=${encodeURIComponent(aurea.tour.emailSubject)}`}
                className="mt-5 inline-flex items-center gap-2 rounded-full border border-terracotta/40 bg-white px-6 py-3 text-sm font-semibold text-terracotta shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:bg-terracotta hover:text-white hover:shadow-md"
              >
                <Mail className="h-4 w-4" /> {aurea.tour.emailLabel}
              </a>
            </div>
          </motion.section>

          {/* WeRoad */}
          <motion.section
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.6 }}
            className="mt-10 rounded-[2rem] border border-ink/10 bg-[#F5EADF] p-8 shadow-[0_18px_50px_-25px_rgba(15,43,70,0.2)] md:p-12"
          >
            <BlockHeading role={weroad.role} title={weroad.title} illustration="/images/collab-weroad.svg" />
            <p className="mt-5 max-w-3xl text-lg leading-relaxed text-ink/75">{weroad.text}</p>
            <div className="mt-7">
              <ExternalLink href={weroad.url}>{weroad.linkLabel}</ExternalLink>
            </div>
          </motion.section>

          <div className="mt-16 flex justify-center">
            <Link to="/" className="group inline-flex items-center gap-3 rounded-full border border-ink/15 bg-white/60 px-6 py-3.5 text-sm font-semibold text-ink/70 shadow-sm backdrop-blur-sm transition-all duration-300 hover:-translate-y-0.5 hover:border-terracotta/40 hover:bg-white hover:text-terracotta hover:shadow-md">
              <ArrowLeft className="h-4 w-4" /> {copy.backHome}
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  )
}
